import { debounce, filter, forEach, isEmpty, isEqual } from 'lodash/fp';
import pt from 'prop-types';
import { Component } from 'react';

import { BreakpointRangeValue } from './types';

export const SUBSCRIBE_CHANNEL = 'breakpointRangeSubscribe';

export interface BreakpointRangeProviderProps<B> {
    getBreakpointRange: () => B;
    resizeDebounce: number;
}

type Subscriber<B> = (breakpointRange: B) => void;

export default class BreakpointRangeProvider<B = BreakpointRangeValue> extends Component<
    BreakpointRangeProviderProps<B>
> {
    static childContextTypes = {
        [SUBSCRIBE_CHANNEL]: pt.func,
    };

    static defaultProps = {
        resizeDebounce: 200,
    };

    private subscribers: Array<Subscriber<B>> = [];
    private previousMatch?: B;

    getChildContext() {
        return {
            [SUBSCRIBE_CHANNEL]: this.subscribe,
        };
    }

    addResizeListener() {
        window.addEventListener('resize', this.handleResize);
    }

    removeResizeListener() {
        this.handleResize.cancel();
        window.removeEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        this.removeResizeListener();
    }

    subscribe = (func: Subscriber<B>) => {
        this.subscribers.push(func);

        if (this.subscribers.length === 1) {
            this.addResizeListener();
        }
        const breakpointRange = this.props.getBreakpointRange();
        func(breakpointRange);

        return () => {
            this.subscribers = filter((f) => f !== func, this.subscribers);
            if (isEmpty(this.subscribers)) {
                this.removeResizeListener();
            }
        };
    };

    updateSubscribers(breakpointRange: B) {
        forEach((f) => f(breakpointRange), this.subscribers);
    }

    handleResize = debounce(this.props.resizeDebounce, () => {
        const breakpointRange = this.props.getBreakpointRange();

        if (!isEqual(this.previousMatch, breakpointRange)) {
            this.updateSubscribers(breakpointRange);
        }

        this.previousMatch = breakpointRange;
    });

    render() {
        return this.props.children;
    }
}
